<template>
  <basic-page title="Asetukset">
    <v-row>
      <v-col cols="12" md="8" lg="6" class="mx-auto">
        <!-- 1. Käyttäjätiedot & Roolit (User Profile & Roles) -->
        <v-card class="mb-5" elevation="2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-account-circle</v-icon>
            Käyttäjätiedot & Roolit
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-list dense>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Nimi</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-medium">
                    {{ userDisplayName }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Sähköposti</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-medium">
                    {{ userEmail }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Käyttäjätunnus (UID)</v-list-item-subtitle>
                  <v-list-item-title class="text-caption grey--text">
                    {{ userUid }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="mb-1">Aktiiviset roolit</v-list-item-subtitle>
                  <div>
                    <template v-if="userRoles && userRoles.length > 0">
                      <v-chip
                        v-for="role in userRoles"
                        :key="role"
                        small
                        class="mr-1 mb-1"
                        color="primary"
                        outlined
                      >
                        {{ $formatRole(role) }}
                      </v-chip>
                    </template>
                    <span v-else class="text-caption grey--text">Ei erikoisrooleja</span>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- 2. Ulkoasu & Teema -->
        <v-card class="mb-5" elevation="2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-palette</v-icon>
            Ulkoasu & Teema
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-list dense>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    Moderni ulkoasu
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Käytä uudistettua vaaleaa ja ilmavaa teemaa
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    :input-value="modernTheme"
                    @change="toggleModernTheme"
                    color="primary"
                    inset
                  ></v-switch>
                </v-list-item-action>
              </v-list-item>

              <v-divider class="my-2" />
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    Tumma teema (Dark Mode)
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{
                      !modernTheme
                        ? "Käytettävissä vain modernissa ulkoasussa"
                        : "Käytä silmille miellyttävää tummaa tilaa"
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    :input-value="darkMode"
                    :disabled="!modernTheme"
                    @change="toggleDarkMode"
                    color="primary"
                    inset
                  ></v-switch>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- 3. Push-ilmoitukset & Testaus -->
        <v-card class="mb-5" elevation="2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-bell-ring</v-icon>
            Push-ilmoitukset
          </v-card-title>
          <v-divider />
          <v-card-text>
            <p class="mb-3">
              Hallitse laitteesi ilmoituslupia ja testaa, että hälytykset,
              kalustonhuoltovuorot ja tehtäväilmoitukset saapuvat laitteellesi.
            </p>

            <v-alert
              :type="notificationStatusType"
              outlined
              dense
              class="mb-4"
            >
              <strong>Ilmoitusten tila laitteella:</strong> {{ notificationStatusText }}
            </v-alert>

            <div class="d-flex flex-wrap gap-2">
              <v-btn
                color="primary"
                rounded
                class="mr-2 mb-2"
                :loading="loadingRequest"
                @click="requestPermission"
              >
                <v-icon left>mdi-bell-plus</v-icon>
                {{
                  notificationPermission === "granted"
                    ? "Päivitä laitetoken"
                    : "Ota ilmoitukset käyttöön"
                }}
              </v-btn>

              <v-btn
                color="secondary"
                outlined
                rounded
                class="mr-2 mb-2"
                :loading="loadingTest"
                :disabled="notificationPermission !== 'granted'"
                @click="sendTestNotification"
              >
                <v-icon left>mdi-send</v-icon>
                Testaa ilmoitusta laitteelle
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- 4. Vianmääritys & Välimuisti (Troubleshooting / Hard refresh) -->
        <v-card class="mb-5" elevation="2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="warning">mdi-wrench</v-icon>
            Vianmääritys & Välimuisti
          </v-card-title>
          <v-divider />
          <v-card-text>
            <p class="text-caption mb-3">
              Jos sovelluksen sisältö ei päivity tai jokin ominaisuus käyttäytyy virheellisesti,
              voit tyhjentää Service Worker -välimuistin ja pakottaa uusimman version latauksen.
            </p>
            <v-btn
              color="error"
              outlined
              rounded
              :loading="loadingClearCache"
              @click="clearCacheAndHardReload"
            >
              <v-icon left>mdi-cached</v-icon>
              Nollaa välimuisti ja lataa uudelleen
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage.vue";
import api from "@/api";
import app, { getToken } from "@/plugins/firebase.app";
import { getMessaging } from "firebase/messaging";

export default {
  name: "Asetukset",
  components: {
    BasicPage,
  },
  data() {
    return {
      isNotificationsSupported: "Notification" in window,
      notificationPermission:
        "Notification" in window ? Notification.permission : "unsupported",
      loadingRequest: false,
      loadingTest: false,
      loadingClearCache: false,
    };
  },
  computed: {
    modernTheme() {
      return this.$store.state.modernTheme;
    },
    darkMode() {
      return this.$store.state.darkMode;
    },
    userDisplayName() {
      return this.$userInfo?.displayName || this.$userInfo?.user || "Nimetön käyttäjä";
    },
    userEmail() {
      return this.$userInfo?.email || "-";
    },
    userUid() {
      return this.$userInfo?.localId || this.$userInfo?.sub || "-";
    },
    userRoles() {
      const roles = this.$userInfo?.customAttributes?.roles || [];
      const quals = this.$userInfo?.customAttributes?.qualifications || [];
      return [...roles, ...quals];
    },
    notificationStatusText() {
      if (!this.isNotificationsSupported) {
        return "Selaimesi tai asennettu laitteesi ei tue WebPush-ilmoituksia.";
      }
      if (this.notificationPermission === "granted") {
        return "Sallittu. Push-ilmoitukset ovat käytössä tällä laitteella.";
      }
      if (this.notificationPermission === "denied") {
        return "Estetty. Olet estänyt ilmoitukset selaimen asetuksissa.";
      }
      return "Ei vielä sallittu. Klikkaa 'Ota ilmoitukset käyttöön'.";
    },
    notificationStatusType() {
      if (!this.isNotificationsSupported || this.notificationPermission === "denied") {
        return "error";
      }
      if (this.notificationPermission === "granted") {
        return "success";
      }
      return "info";
    },
  },
  mounted() {
    if ("Notification" in window) {
      this.notificationPermission = Notification.permission;
    }
  },
  methods: {
    toggleModernTheme() {
      this.$store.dispatch("toggleModernTheme");
    },
    toggleDarkMode() {
      this.$store.dispatch("toggleDarkMode");
    },
    async requestPermission() {
      if (!("Notification" in window)) {
        this.$toast("Selaimesi ei tue ilmoituksia.", { type: "info" });
        return;
      }
      this.loadingRequest = true;
      try {
        localStorage.removeItem("notificationPermissionDenied");
        const permission = await Notification.requestPermission();
        this.notificationPermission = permission;

        if (permission !== "granted") {
          this.$toast("Ilmoituslupaa ei myönnetty.", { type: "warning" });
          return;
        }

        const messaging = getMessaging(app);
        const serviceWorkerRegistration = await navigator.serviceWorker.ready;
        const token = await getToken(messaging, {
          vapidKey:
            "BCZ3S_HG6b-bUNUJkxygFPTu_UDHfthcEcFmCODFxCTVNNVNRbZtRhYWcNnK7DMkTWAfLEopWOynqpOb_pcJyi0",
          serviceWorkerRegistration,
        });

        if (token) {
          await api.firebaseMessagingAddToken({
            token,
            userAgent: navigator.userAgent,
          });
          this.$toast("Ilmoitukset ja laitetoken päivitetty onnistuneesti!", {
            type: "success",
          });
        } else {
          this.$toast("Tokenin haku epäonnistui.", { type: "error" });
        }
      } catch (e) {
        console.error("[Asetukset] requestPermission error:", e);
        this.$toast("Virhe ilmoitusten käyttöönotossa.", { type: "error" });
      } finally {
        this.loadingRequest = false;
        if ("Notification" in window) {
          this.notificationPermission = Notification.permission;
        }
      }
    },
    async sendTestNotification() {
      this.loadingTest = true;
      try {
        const res = await api.sendTestNotificationToSelf();
        if (res?.data?.success) {
          this.$toast("Testi-ilmoitus lähetetty! Sen pitäisi saapua hetken kuluttua.", {
            type: "success",
          });
        } else {
          this.$toast("Testi-ilmoituksen lähetys epäonnistui: " + (res?.data?.error || "tuntematon syy"), {
            type: "error",
          });
        }
      } catch (e) {
        console.error("[Asetukset] sendTestNotification error:", e);
        this.$toast("Virhe testi-ilmoituksen kutsussa.", { type: "error" });
      } finally {
        this.loadingTest = false;
      }
    },
    async clearCacheAndHardReload() {
      this.loadingClearCache = true;
      try {
        // 1. Clear CacheStorage
        if ("caches" in window) {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map((key) => caches.delete(key)));
        }

        // 2. Unregister Service Workers
        if ("serviceWorker" in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((reg) => reg.unregister()));
        }

        this.$toast("Välimuisti tyhjennetty. Ladataan sovellus uudelleen...", {
          type: "info",
        });

        setTimeout(() => {
          window.location.reload(true);
        }, 1000);
      } catch (e) {
        console.error("[Asetukset] clearCache error:", e);
        this.$toast("Virhe välimuistin tyhjennyksessä.", { type: "error" });
        this.loadingClearCache = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>

